import React, {useEffect, useRef, useState} from 'react';
import {makeRequest} from "../../utils/httpRequest";
import Config from "../../core/config";
import {formatDateString} from "../../utils/utils";
import Cookies from "js-cookie";
import Loading from "../../core/Loading";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const apiUrl = Config.apiUrl;

const RentalViewer = ({id, isBudget, onClose}) => {
    const [rental, setRental] = useState(null);
    const [loading, setLoading] = useState(true);
    const [generatingPdf, setGeneratingPdf] = useState(false);
    const viewerRef = useRef(null);

    useEffect(() => {
        loadRental();
    }, [id]);

    async function loadRental() {
        try {
            setLoading(true);
            const url = apiUrl + '/api/rentals/id=' + id;
            const response = await makeRequest(url, 'GET', {'Useruuid': Cookies.get('userUUID')});
            setRental(response);
        } catch (error) {
            console.error('Error loading rental:', error);
            alert('Erro ao carregar dados do ' + (isBudget ? 'orçamento' : 'aluguel'));
        } finally {
            setLoading(false);
        }
    }

    async function handleGeneratePdf() {
        if (generatingPdf || !viewerRef.current) {
            return;
        }

        try {
            setGeneratingPdf(true);
            const scale = Math.min(3, window.devicePixelRatio ? window.devicePixelRatio + 1 : 2.5);
            const canvas = await html2canvas(viewerRef.current, {
                scale,
                useCORS: true,
                windowWidth: viewerRef.current.scrollWidth,
                scrollY: -window.scrollY,
                backgroundColor: '#ffffff'
            });

            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF('p', 'pt', 'a4');
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const imgProps = pdf.getImageProperties(imgData);
            const imgHeight = (imgProps.height * pdfWidth) / imgProps.width;
            const pdfHeight = pdf.internal.pageSize.getHeight();

            let heightLeft = imgHeight;
            let position = 0;

            pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, imgHeight);
            heightLeft -= pdfHeight;

            while (heightLeft > 0) {
                position = heightLeft - imgHeight;
                pdf.addPage();
                pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, imgHeight);
                heightLeft -= pdfHeight;
            }

            pdf.save(`orcamento_${rental.code}.pdf`);
        } catch (error) {
            console.error('Error generating PDF:', error);
            alert('Erro ao gerar o PDF do orçamento. Tente novamente.');
        } finally {
            setGeneratingPdf(false);
        }
    }

    if (loading) {
        return <Loading />;
    }

    if (!rental) {
        return <div>Erro ao carregar dados</div>;
    }

    return (
        <div style={{padding: '20px', maxWidth: '800px', margin: '0 auto'}}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
                <div className="header-label">
                    Visualizar {isBudget ? 'Orçamento' : 'Aluguel'} - {rental.code}
                </div>
                {isBudget && (
                    <button onClick={handleGeneratePdf} disabled={generatingPdf}>
                        {generatingPdf ? 'Gerando...' : 'Gerar PDF'}
                    </button>
                )}
            </div>

            <div ref={viewerRef}>
                {isBudget && (
                    <div style={{textAlign: 'center', marginBottom: '20px'}}>
                        <h2 style={{margin: 0, fontSize: '24px'}}>Orçamento</h2>
                    </div>
                )}

                {/* Informações do Cliente */}
                <div style={{marginBottom: '20px'}}>
                    <h3>Cliente</h3>
                    <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px'}}>
                        <div>
                            <strong>Nome:</strong> {rental.customer?.name}
                        </div>
                        <div>
                            <strong>Telefone:</strong> {rental.customer?.phone}
                        </div>
                        <div>
                            <strong>Email:</strong> {rental.customer?.email}
                        </div>
                        <div>
                            <strong>CPF/CNPJ:</strong> {rental.customer?.cpfCnpj}
                        </div>
                    </div>
                </div>

                {/* Informações do Aluguel/Orçamento */}
                <div style={{marginBottom: '20px'}}>
                    <h3>Informações Gerais</h3>
                    <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px'}}>
                        <div>
                            <strong>Data:</strong> {formatDateString(rental.rentalDate)}
                        </div>
                    </div>
                </div>

                {/* Endereço */}
                {rental.address && (
                    <div style={{marginBottom: '20px'}}>
                        <h3>Endereço de Entrega</h3>
                        <div>
                            {rental.address}
                            {rental.addressNumber && `, ${rental.addressNumber}`}
                            {rental.addressComplement && ` - ${rental.addressComplement}`}
                            {rental.neighborhood && ` - ${rental.neighborhood}`}
                            {rental.city && `, ${rental.city}`}
                            {rental.state && ` - ${rental.state}`}
                            {rental.cep && `, CEP: ${rental.cep}`}
                        </div>
                    </div>
                )}

                {/* Itens */}
                <div style={{marginBottom: '20px'}}>
                    <h3>Itens</h3>
                    <table style={{width: '100%', borderCollapse: 'collapse'}}>
                        <thead>
                            <tr style={{backgroundColor: '#f5f5f5'}}>
                                <th style={{border: '1px solid #ddd', padding: '8px', textAlign: 'left'}}>Produto</th>
                                <th style={{border: '1px solid #ddd', padding: '8px', textAlign: 'center'}}>Quantidade</th>
                                <th style={{border: '1px solid #ddd', padding: '8px', textAlign: 'center'}}>Dias</th>
                                <th style={{border: '1px solid #ddd', padding: '8px', textAlign: 'center'}}>Data Entrega</th>
                                <th style={{border: '1px solid #ddd', padding: '8px', textAlign: 'center'}}>Data Devolução</th>
                                <th style={{border: '1px solid #ddd', padding: '8px', textAlign: 'right'}}>Valor Unit.</th>
                                <th style={{border: '1px solid #ddd', padding: '8px', textAlign: 'right'}}>Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rental.rentalItems?.map((item, index) => (
                                <tr key={index}>
                                    <td style={{border: '1px solid #ddd', padding: '8px'}}>{item.product?.name}</td>
                                    <td style={{border: '1px solid #ddd', padding: '8px', textAlign: 'center'}}>{item.quantity}</td>
                                    <td style={{border: '1px solid #ddd', padding: '8px', textAlign: 'center'}}>{item.rentalDays}</td>
                                    <td style={{border: '1px solid #ddd', padding: '8px', textAlign: 'center'}}>{formatDateString(item.rentalDate)}</td>
                                    <td style={{border: '1px solid #ddd', padding: '8px', textAlign: 'center'}}>{formatDateString(item.returnDate)}</td>
                                    <td style={{border: '1px solid #ddd', padding: '8px', textAlign: 'right'}}>R$ {item.value?.toLocaleString('pt-BR', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2
                                    })}</td>
                                    <td style={{border: '1px solid #ddd', padding: '8px', textAlign: 'right'}}>R$ {item.total?.toLocaleString('pt-BR', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2
                                    })}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Valores */}
                <div style={{marginBottom: '20px'}}>
                    <div style={{display: 'flex', gap: '20px', alignItems: 'center'}}>
                        <div>
                            <strong>Valor (Itens):</strong> R$ {(rental.totalValue - (rental.shippingCost || 0))?.toLocaleString('pt-BR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                            })}
                        </div>
                        <div>
                            <strong>Frete:</strong> R$ {(rental.shippingCost || 0)?.toLocaleString('pt-BR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                            })}
                        </div>
                        <div style={{borderLeft: '1px solid #ddd', paddingLeft: '20px'}}>
                            <strong>Valor Total:</strong> R$ {rental.totalValue?.toLocaleString('pt-BR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                            })}
                        </div>
                    </div>
                </div>

                {/* Formas de Pagamento */}
                {rental.paymentMethods && rental.paymentMethods.length > 0 && (
                    <div style={{marginBottom: '20px'}}>
                        <h3>Formas de Pagamento</h3>
                        <table style={{width: '100%', borderCollapse: 'collapse'}}>
                            <thead>
                                <tr style={{backgroundColor: '#f5f5f5'}}>
                                    <th style={{border: '1px solid #ddd', padding: '8px', textAlign: 'left'}}>Forma de Pagamento</th>
                                    <th style={{border: '1px solid #ddd', padding: '8px', textAlign: 'right'}}>Valor</th>
                                    <th style={{border: '1px solid #ddd', padding: '8px', textAlign: 'center'}}>Vencimento</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rental.paymentMethods.map((payment, index) => (
                                    <tr key={index}>
                                        <td style={{border: '1px solid #ddd', padding: '8px'}}>{payment.paymentMethod?.name}</td>
                                        <td style={{border: '1px solid #ddd', padding: '8px', textAlign: 'right'}}>R$ {payment.value?.toLocaleString('pt-BR', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2
                                        })}</td>
                                        <td style={{border: '1px solid #ddd', padding: '8px', textAlign: 'center'}}>{formatDateString(payment.dueDate)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Observações */}
                {rental.details && (
                    <div style={{marginBottom: '20px'}}>
                        <h3>Observações</h3>
                        <div style={{padding: '10px', backgroundColor: '#f9f9f9', border: '1px solid #ddd', borderRadius: '4px'}}>
                            {rental.details}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default RentalViewer; 